/**
 * Testimonial grid helpers (SHOP-21 + SHOP-23):
 * - Shortest-column masonry pack for desktop/tablet
 * - Desktop progressive reveal ("Weitere Bewertungen")
 * - Mobile 2-row carousel auto-scroll (pause on user input)
 */
(function () {
  const GAP = 4;
  const MOBILE_MQ = '(max-width: 599px)';
  const DESKTOP_MQ = '(min-width: 1000px)';
  const NATIVE_MASONRY = CSS.supports('grid-template-rows', 'masonry');
  const REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const AUTO_SCROLL_IDLE_MS = 4200;
  const AUTO_SCROLL_INTERVAL_MS = 3200;

  function columnCount() {
    if (window.matchMedia(MOBILE_MQ).matches) return 0;
    return window.matchMedia(DESKTOP_MQ).matches ? 4 : 2;
  }

  function isIntroItem(el) {
    return Boolean(el.querySelector('.testimonial-intro'));
  }

  function reviewItems(grid) {
    return [...grid.children].filter((el) => !isIntroItem(el));
  }

  function clearPack(grid) {
    grid.classList.remove('testimonial-grid--packed');
    grid.classList.remove('testimonial-grid--measuring');
    grid.style.height = '';
    grid.style.position = '';
    [...grid.children].forEach((el) => {
      if (el.hasAttribute('data-e1-review-deferred')) return;
      el.style.position = '';
      el.style.top = '';
      el.style.left = '';
      el.style.width = '';
      el.style.margin = '';
    });
  }

  function pack(grid) {
    if (grid.dataset.masonryLock === '1') return;

    if (NATIVE_MASONRY) {
      clearPack(grid);
      return;
    }

    const cols = columnCount();
    if (!cols) {
      clearPack(grid);
      return;
    }

    const items = [...grid.children].filter((el) => !el.hasAttribute('data-e1-review-deferred'));
    if (!items.length) return;

    grid.dataset.masonryLock = '1';

    try {
      clearPack(grid);
      grid.classList.add('testimonial-grid--measuring');
      grid.style.position = 'relative';

      const gridWidth = grid.clientWidth;
      if (gridWidth < 1) return;

      const colWidth = (gridWidth - GAP * (cols - 1)) / cols;

      items.forEach((el) => {
        el.style.position = 'relative';
        el.style.top = 'auto';
        el.style.left = 'auto';
        el.style.width = `${colWidth}px`;
        el.style.margin = '0';
      });

      const heights = items.map((el) => el.getBoundingClientRect().height);

      grid.classList.remove('testimonial-grid--measuring');
      grid.classList.add('testimonial-grid--packed');

      const colHeights = Array(cols).fill(0);
      items.forEach((el, i) => {
        let col = 0;
        for (let c = 1; c < cols; c++) {
          if (colHeights[c] < colHeights[col]) col = c;
        }
        const top = colHeights[col];
        const left = col * (colWidth + GAP);
        el.style.position = 'absolute';
        el.style.top = `${top}px`;
        el.style.left = `${left}px`;
        el.style.width = `${colWidth}px`;
        el.style.margin = '0';
        colHeights[col] = top + heights[i] + GAP;
      });

      grid.style.height = `${Math.max(...colHeights, GAP) - GAP}px`;
    } finally {
      requestAnimationFrame(() => {
        grid.dataset.masonryLock = '0';
      });
    }
  }

  function clearMobileTileHeight(grid) {
    grid.style.removeProperty('--e1-mobile-tile-h');
  }

  /**
   * Mobile 2-row carousel: lock both grid rows to the tallest natural card
   * height so stacked kacheln share one height (no masonry holes).
   *
   * Important: do NOT clear --e1-mobile-tile-h before measuring. Clearing
   * shrinks the grid, ResizeObserver re-enters schedulePack mid-frame, and
   * the style attribute can be left empty (unequal tiles). Measure with a
   * temporary height:auto on cards, then write the var only when it changes.
   */
  function equalizeMobileTiles(grid) {
    if (grid.dataset.equalizeLock === '1') return;

    if (!window.matchMedia(MOBILE_MQ).matches) {
      clearMobileTileHeight(grid);
      return;
    }

    const cards = [...grid.querySelectorAll('.testimonial-card')];
    if (!cards.length) return;

    grid.dataset.equalizeLock = '1';

    try {
      const prev = grid.style.getPropertyValue('--e1-mobile-tile-h').trim();

      // Force content-sized measure even while rows are already locked tall.
      for (const card of cards) {
        card.style.setProperty('height', 'auto', 'important');
      }

      let max = 0;
      for (const card of cards) {
        const h = card.getBoundingClientRect().height;
        if (h > max) max = h;
      }

      for (const card of cards) {
        card.style.removeProperty('height');
      }

      if (max > 0) {
        const next = `${Math.ceil(max)}px`;
        if (prev !== next) {
          grid.style.setProperty('--e1-mobile-tile-h', next);
        }
      }
    } finally {
      grid.dataset.equalizeLock = '0';
    }
  }

  function schedulePack(grid) {
    if (grid.dataset.masonryLock === '1') return;
    if (grid.dataset.equalizeLock === '1') return;
    cancelAnimationFrame(Number(grid.dataset.masonryFrame || 0));
    const frame = requestAnimationFrame(() => {
      equalizeMobileTiles(grid);
      pack(grid);
    });
    grid.dataset.masonryFrame = String(frame);
  }

  function createLoadMoreButton(grid) {
    const template = document.getElementById('e1-reviews-load-more-template');
    if (template && template.content) {
      const source = template.content.querySelector('[data-e1-reviews-load-more]');
      if (source) return source.cloneNode(true);
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className =
      'e1-reviews-load-more e1-reviews-load-more--beside-klaviyo inline-flex items-center justify-center px-8 py-4 bg-transparent rounded-full text-base font-semibold tracking-[-0.04em] leading-none border transition-opacity hover:opacity-80 w-fit';
    button.setAttribute('data-e1-reviews-load-more', '');
    button.hidden = true;
    button.textContent =
      grid.dataset.e1ReviewsLoadMoreLabel || 'Weitere Bewertungen';
    return button;
  }

  /**
   * Desktop: mount "Weitere Bewertungen" beside Klaviyo's write-review CTA
   * (`.kl_reviews__button_bar` lives in a separate app-block section).
   * Button is never rendered in the intro card.
   */
  function mountLoadMoreBesideKlaviyo(grid) {
    const bar = document.querySelector('.kl_reviews__button_bar');
    if (!bar) return false;

    let button = bar.querySelector('[data-e1-reviews-load-more]');
    if (!button) {
      button = createLoadMoreButton(grid);
      button.classList.add('e1-reviews-load-more--beside-klaviyo');
      bar.appendChild(button);
    }

    bar.classList.add('e1-reviews-load-more-host');
    return true;
  }

  function watchKlaviyoButtonBar(grid) {
    if (grid.dataset.klaviyoLoadMoreWatch === '1') return;
    grid.dataset.klaviyoLoadMoreWatch = '1';

    const tryMount = () => {
      if (!mountLoadMoreBesideKlaviyo(grid)) return false;
      syncLoadMore(grid);
      return true;
    };

    if (tryMount()) return;

    const observer = new MutationObserver(() => {
      if (tryMount()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 30000);
  }

  function syncLoadMore(grid) {
    mountLoadMoreBesideKlaviyo(grid);
    const button = document.querySelector('.kl_reviews__button_bar [data-e1-reviews-load-more]');
    if (!button) return;

    const deferred = reviewItems(grid).filter((el) => el.hasAttribute('data-e1-review-deferred'));
    const isMobile = window.matchMedia(MOBILE_MQ).matches;

    if (isMobile || deferred.length === 0) {
      button.hidden = true;
      button.setAttribute('aria-hidden', 'true');
      return;
    }

    button.hidden = false;
    button.removeAttribute('aria-hidden');
  }

  function applyDesktopBatching(grid) {
    const cards = reviewItems(grid);
    const initial = Number(grid.dataset.e1ReviewsInitial || 7);
    const isMobile = window.matchMedia(MOBILE_MQ).matches;

    cards.forEach((el, index) => {
      if (isMobile || index < initial) {
        el.removeAttribute('data-e1-review-deferred');
      } else if (!el.hasAttribute('data-e1-review-revealed')) {
        el.setAttribute('data-e1-review-deferred', '');
      }
    });

    syncLoadMore(grid);
  }

  function revealNextBatch(grid) {
    const batch = Number(grid.dataset.e1ReviewsBatch || 8);
    const deferred = reviewItems(grid).filter((el) => el.hasAttribute('data-e1-review-deferred'));
    deferred.slice(0, batch).forEach((el) => {
      el.removeAttribute('data-e1-review-deferred');
      el.setAttribute('data-e1-review-revealed', '');
    });
    syncLoadMore(grid);
    schedulePack(grid);
  }

  function bindLoadMore(grid) {
    if (grid.dataset.loadMoreBound === '1') return;
    grid.dataset.loadMoreBound = '1';

    // Document delegation so the handler works after mounting into Klaviyo's bar.
    if (document.documentElement.dataset.e1ReviewsLoadMoreDelegated !== '1') {
      document.documentElement.dataset.e1ReviewsLoadMoreDelegated = '1';
      document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-e1-reviews-load-more]');
        if (!button) return;
        event.preventDefault();
        const targetGrid =
          document.querySelector('#e1-testimonials.testimonial-grid') ||
          document.querySelector('.testimonial-grid[data-e1-reviews-grid]') ||
          document.querySelector('.testimonial-grid');
        if (targetGrid) revealNextBatch(targetGrid);
      });
    }

    watchKlaviyoButtonBar(grid);
  }

  function columnStep(grid) {
    const styles = getComputedStyle(grid);
    const autoCol = styles.gridAutoColumns;
    const parsed = Number.parseFloat(autoCol);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 14;
    const firstCard = [...grid.children].find((el) => !isIntroItem(el));
    const width = Number.isFinite(parsed) && parsed > 0
      ? parsed
      : firstCard
        ? firstCard.getBoundingClientRect().width
        : grid.clientWidth * 0.8;
    return Math.max(width + gap, 1);
  }

  function bindMobileAutoScroll(grid) {
    if (grid.dataset.autoScrollBound === '1') return;
    grid.dataset.autoScrollBound = '1';

    let paused = false;
    let resumeTimer = 0;
    let intervalId = 0;

    const pause = () => {
      paused = true;
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(() => {
        paused = false;
      }, AUTO_SCROLL_IDLE_MS);
    };

    grid.addEventListener('touchstart', pause, { passive: true });
    grid.addEventListener('touchmove', pause, { passive: true });
    grid.addEventListener('pointerdown', pause);
    grid.addEventListener('wheel', pause, { passive: true });

    // Step by one column so scroll-snap does not cancel sub-pixel auto-scroll.
    const advance = () => {
      if (!window.matchMedia(MOBILE_MQ).matches) return;
      if (paused || REDUCE_MOTION.matches) return;

      const max = grid.scrollWidth - grid.clientWidth;
      if (max <= 4) return;

      if (grid.scrollLeft >= max - 2) {
        grid.scrollTo({ left: 0, behavior: REDUCE_MOTION.matches ? 'auto' : 'smooth' });
        return;
      }

      grid.scrollBy({
        left: columnStep(grid),
        behavior: 'smooth',
      });
    };

    intervalId = window.setInterval(advance, AUTO_SCROLL_INTERVAL_MS);

    grid.addEventListener(
      'shopify:section:unload',
      () => {
        window.clearInterval(intervalId);
        window.clearTimeout(resumeTimer);
      },
      { once: true }
    );
  }

  function bind(grid) {
    if (grid.dataset.masonryBound === '1') {
      applyDesktopBatching(grid);
      schedulePack(grid);
      return;
    }
    grid.dataset.masonryBound = '1';

    applyDesktopBatching(grid);
    bindLoadMore(grid);
    bindMobileAutoScroll(grid);
    schedulePack(grid);

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => schedulePack(grid));
      ro.observe(grid);
    }

    window.matchMedia(MOBILE_MQ).addEventListener('change', () => {
      applyDesktopBatching(grid);
      schedulePack(grid);
    });
    window.matchMedia(DESKTOP_MQ).addEventListener('change', () => schedulePack(grid));
    window.addEventListener('load', () => schedulePack(grid), { once: true });

    grid.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', () => schedulePack(grid), { once: true });
    });
  }

  function init() {
    document.querySelectorAll('.testimonial-grid').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
})();
